import type React from "react";
import type { ComponentType } from "react";

import type { CSSProperties as LinariaCSSProperties } from "@linaria/core";
import { css, cx } from "@linaria/core";
import { styled as linariaStyled } from "@linaria/react";

import { withTheme } from "./theme/themeProvider";
import type { UseThemeType } from "./theme/types";

export type DefaultTheme = UseThemeType;
export type CSSProperties = LinariaCSSProperties;

type ThemeProps = { theme: DefaultTheme };

export type WYWMeta = { className: string; extends?: unknown };

export type WYWEvalMeta = { __wyw_meta: WYWMeta };

type StyledMetaSource = Partial<WYWEvalMeta> | string;

type IntrinsicElements = React.JSX.IntrinsicElements & JSX.IntrinsicElements;

type Simplify<T> = { [K in keyof T]: T[K] } & {};

export type StyledPropsCarrier<TProps> = { readonly __sdoStyledProps: TProps };

type ComponentPropsOf<T> =
  T extends StyledPropsCarrier<infer P>
    ? P
    : T extends React.ComponentType<infer P>
      ? P
      : T extends React.ExoticComponent<infer P>
        ? P
        : never;

/**
 * A styled result deliberately has one callable layer. Intersecting the new
 * ComponentType with its target preserves every previous overload and makes
 * repeated styled(StyledX) expressions grow exponentially.
 */
type StyledComponentType<TProps> = WYWEvalMeta &
  React.FunctionComponent<Simplify<TProps>> &
  StyledPropsCarrier<Simplify<TProps>>;

type StaticPlaceholder = string | number | LinariaCSSProperties | WYWEvalMeta;

type WithOptionalTheme<T> = Simplify<
  T & { as?: React.ElementType; theme?: DefaultTheme } & Record<string, unknown>
>;

type HtmlStyledTag<TName extends keyof IntrinsicElements> = <
  TAdditionalProps = Record<never, unknown>,
>(
  strings: TemplateStringsArray,
  ...exprs: Array<
    | StaticPlaceholder
    | ((
        props: IntrinsicElements[TName] &
          NoInfer<TAdditionalProps> &
          ThemeProps,
      ) => string | number | false | null | undefined)
  >
) => StyledComponentType<
  WithOptionalTheme<IntrinsicElements[TName] & TAdditionalProps>
>;

export type StyledJSXIntrinsics = {
  readonly [P in keyof IntrinsicElements]: HtmlStyledTag<P>;
};

type ComponentStyledTag<TComponent> = <
  TAdditionalProps extends Record<string, unknown> = Record<never, unknown>,
>(
  strings: TemplateStringsArray,
  ...exprs: Array<
    | StaticPlaceholder
    | ((
        props: Simplify<
          ComponentPropsOf<TComponent> & NoInfer<TAdditionalProps> & ThemeProps
        >,
      ) => string | number | false | null | undefined)
  >
) => StyledComponentType<
  Simplify<
    ComponentPropsOf<TComponent> & TAdditionalProps & { theme?: DefaultTheme }
  >
>;

declare function styledCallable<TComponent>(
  component: TComponent,
): ComponentStyledTag<TComponent>;

declare function styledCallable<TName extends keyof IntrinsicElements>(
  tag: TName,
): HtmlStyledTag<TName>;

export type Styled = typeof styledCallable & StyledJSXIntrinsics;

type ThemedComponent = ComponentType<Record<string, unknown>> &
  Partial<WYWEvalMeta>;

/**
 * Wrap Linaria styled with theme injection, but keep `__wyw_meta` so other
 * styled templates can target this component via `${StyledX} { … }`.
 */
function wrapWithTheme<C extends ThemedComponent>(Component: C): C {
  const Themed = withTheme(
    Component as ComponentType<{ theme: UseThemeType }>,
  ) as C;
  if (Component.__wyw_meta != null) {
    Themed.__wyw_meta = Component.__wyw_meta;
  }
  Themed.displayName = Component.displayName;
  return Themed;
}

/**
 * Plain React targets of `styled(FooComponent)` have no `__wyw_meta`. Linaria
 * then treats `as` as a replacement tag (renders `<a>` instead of calling
 * FooComponent). Stamping a placeholder makes Linaria forward `as` into the
 * component — the empty public wrap stays a wyw `${Foo}` / `styled(Foo)`
 * anchor only. Do not overwrite real meta on already-styled targets.
 */
function ensureAsForwardingMeta(component: ThemedComponent): void {
  if (component.__wyw_meta == null) {
    component.__wyw_meta = { className: "" };
  }
}

function createThemedTag(tagOrComponent: string | ThemedComponent) {
  if (typeof tagOrComponent !== "string") {
    ensureAsForwardingMeta(tagOrComponent);
  }

  const factory =
    typeof tagOrComponent === "string"
      ? linariaStyled(tagOrComponent as keyof JSX.IntrinsicElements)
      : linariaStyled(tagOrComponent);

  return (strings: TemplateStringsArray, ...values: unknown[]) =>
    wrapWithTheme(factory(strings, ...(values as never[])) as ThemedComponent);
}

const styledBase = (tagOrComponent: string | ThemedComponent) =>
  createThemedTag(tagOrComponent);

export const styled = new Proxy(styledBase, {
  get(_target, prop: string | symbol) {
    if (typeof prop === "string") {
      return createThemedTag(prop);
    }
    return undefined;
  },
}) as Styled;

export { css, cx };
